import { Injectable, signal } from '@angular/core';

/**
 * Evento proprietário do Chromium (não existe na tipagem do DOM). O navegador o dispara quando o
 * site cumpre os critérios de instalação; guardá-lo é o que permite abrir o diálogo depois, no
 * clique do usuário.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** Como a instalação pode ser oferecida nesta combinação de navegador e sistema. */
export type PwaInstallMode =
  /** Chromium: dá para abrir o diálogo nativo por código. */
  | 'prompt'
  /** iOS: a Apple não expõe instalação por código — só resta ensinar o gesto. */
  | 'ios-instructions'
  /** Já instalado, sem suporte, ou o navegador ainda não considerou o site instalável. */
  | 'none';

/**
 * Oferta de instalação do PWA.
 *
 * O caminho nativo do navegador existe (ícone na barra de endereço no Chrome, `… > Aplicativos` no
 * Edge, `⋮ > Instalar app` no Android), mas está escondido o bastante para que o usuário de campo
 * nunca o encontre — sem um convite na tela, a feature fica instalada em ninguém.
 *
 * O serviço precisa ser instanciado **no arranque**, e não quando o botão aparece: o
 * `beforeinstallprompt` é disparado cedo e uma só vez. Quem escuta tarde demais não recebe nada.
 */
@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  /** Guardado até o usuário clicar: `prompt()` só pode ser chamado a partir de um gesto dele. */
  private deferredPrompt: BeforeInstallPromptEvent | null = null;

  readonly mode = signal<PwaInstallMode>('none');

  init(): void {
    if (typeof window === 'undefined') return;

    // Rodando como app instalado: não faz sentido oferecer instalação de novo.
    if (this.isRunningStandalone()) {
      this.mode.set('none');
      return;
    }

    if (this.isIos()) {
      this.mode.set('ios-instructions');
      return;
    }

    window.addEventListener('beforeinstallprompt', (event: Event) => {
      // Sem o preventDefault o Chromium mostra a própria barra de instalação; segurando o evento,
      // o convite acontece no nosso botão, onde o usuário de fato olha.
      event.preventDefault();
      this.deferredPrompt = event as BeforeInstallPromptEvent;
      this.mode.set('prompt');
    });

    window.addEventListener('appinstalled', () => {
      this.deferredPrompt = null;
      this.mode.set('none');
    });
  }

  /**
   * Abre o diálogo do navegador. O evento é de **uso único**: aceito ou recusado, some. Recusando,
   * o botão desaparece em vez de continuar clicável sem efeito — o navegador só reemite o evento
   * quando quiser, e um botão que não faz nada é pior que botão nenhum.
   */
  async promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    const event = this.deferredPrompt;
    if (!event) return 'unavailable';

    this.deferredPrompt = null;
    await event.prompt();
    const { outcome } = await event.userChoice;
    this.mode.set('none');
    return outcome;
  }

  /** Já aberto como app (tela cheia), seja pelo manifest (Android/desktop) ou pelo iOS. */
  private isRunningStandalone(): boolean {
    const iosStandalone = (window.navigator as { standalone?: boolean }).standalone === true;
    return iosStandalone || window.matchMedia('(display-mode: standalone)').matches;
  }

  /** iPad moderno se declara como Mac: a checagem de toque é o que o separa de um desktop. */
  private isIos(): boolean {
    const ua = window.navigator.userAgent;
    const isIphoneOrIpad = /iPad|iPhone|iPod/.test(ua);
    const isIpadOnMacUa = ua.includes('Macintosh') && navigator.maxTouchPoints > 1;
    return isIphoneOrIpad || isIpadOnMacUa;
  }
}
